import React, { useState, useEffect, useRef } from 'react';
import { heroData as mockHeroData } from '../data/mock';
import { gamesData as mockGamesData } from '../data/mock';

const HeroSection = ({ data, gameData }) => {
  const heroData = data || mockHeroData;
  const gamesData = gameData || mockGamesData;
  const [trail, setTrail] = useState([]);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const containerRef = useRef(null);
  const trailRef = useRef([]);
  const trailIndexRef = useRef(0);
  const lastAddTimeRef = useRef(0);
  const pointerDownRef = useRef(false);
  const rafIdRef = useRef(null);
  const rafScheduledRef = useRef(false);
  const addDelayMs = isCoarsePointer ? 180 : 105;
  const maxItems = isCoarsePointer ? 8 : 15;
  const maxAgeMs = isCoarsePointer ? 2200 : 6000;
  const imageSize = isCoarsePointer ? 140 : 220;

  useEffect(() => {
    const coarseQuery = window.matchMedia('(pointer: coarse)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const params = new URLSearchParams(window.location.search);

    setIsCoarsePointer(coarseQuery.matches);
    setPrefersReducedMotion(motionQuery.matches);
    setShowDebug(params.get('debugTrail') === '1');

    const handleCoarseChange = (event) => setIsCoarsePointer(event.matches);
    const handleMotionChange = (event) => setPrefersReducedMotion(event.matches);

    coarseQuery.addEventListener('change', handleCoarseChange);
    motionQuery.addEventListener('change', handleMotionChange);

    return () => {
      coarseQuery.removeEventListener('change', handleCoarseChange);
      motionQuery.removeEventListener('change', handleMotionChange);
    };
  }, []);

  useEffect(() => {
    const target = containerRef.current;
    if (!target || prefersReducedMotion) {
      trailRef.current = [];
      setTrail([]);
      return;
    }

    const flushTrailToState = () => {
      rafScheduledRef.current = false;
      const now = Date.now();
      trailRef.current = trailRef.current.filter((item) => now - item.createdAt < maxAgeMs);
      setTrail([...trailRef.current]);
    };

    const scheduleFlush = () => {
      if (rafScheduledRef.current) return;
      rafScheduledRef.current = true;
      rafIdRef.current = window.requestAnimationFrame(flushTrailToState);
    };

    const addTrailItem = (e) => {
      const now = Date.now();
      if (now - lastAddTimeRef.current < addDelayMs) return;
      if (isCoarsePointer && !pointerDownRef.current) return;
      lastAddTimeRef.current = now;

      const rect = target.getBoundingClientRect();
      const clientX = e.clientX;
      const clientY = e.clientY;

      const shouldUseImage = trailIndexRef.current % 2 === 0;
      let finalItem;
      let finalIsImage;

      if (shouldUseImage && heroData.landmarks.length > 0) {
        const landmarkIndex = Math.floor(trailIndexRef.current / 2) % heroData.landmarks.length;
        finalItem = heroData.landmarks[landmarkIndex];
        finalIsImage = true;
      } else {
        const phraseIndex = Math.floor(trailIndexRef.current / 2) % heroData.phrases.length;
        finalItem = { text: heroData.phrases[phraseIndex] };
        finalIsImage = false;
      }

      trailRef.current.push({
        id: `${now}-${Math.random()}`,
        createdAt: now,
        x: clientX - rect.left,
        y: clientY - rect.top,
        content: finalItem,
        isImage: finalIsImage,
        zIndex: Math.random() > 0.5 ? 20 : 0
      });

      trailRef.current = trailRef.current.slice(-maxItems);
      trailIndexRef.current += 1;
      scheduleFlush();
    };

    const handlePointerDown = () => {
      pointerDownRef.current = true;
    };

    const handlePointerUp = () => {
      pointerDownRef.current = false;
    };

    target.addEventListener('pointerdown', handlePointerDown, { passive: true });
    target.addEventListener('pointermove', addTrailItem, { passive: true });
    window.addEventListener('pointerup', handlePointerUp, { passive: true });
    window.addEventListener('pointercancel', handlePointerUp, { passive: true });

    const pruneInterval = window.setInterval(() => {
      if (trailRef.current.length > 0) scheduleFlush();
    }, 200);

    return () => {
      target.removeEventListener('pointerdown', handlePointerDown);
      target.removeEventListener('pointermove', addTrailItem);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      window.clearInterval(pruneInterval);
      if (rafIdRef.current) window.cancelAnimationFrame(rafIdRef.current);
      rafScheduledRef.current = false;
    };
  }, [heroData, isCoarsePointer, prefersReducedMotion, addDelayMs, maxItems, maxAgeMs]);

  return (
    <section
      id="hero"
      ref={containerRef}
      className="relative min-h-screen bg-gradient-to-br from-[#F5F5F0] to-[#E8E8DC] overflow-hidden flex items-center touch-pan-y"
    >
      {/* Trail elements - bigger and more spaced with random z-index */}
      {trail.map((item) => {
        const age = Date.now() - item.createdAt;
        const opacity = Math.max(0, 1 - age / maxAgeMs);
        
        return (
          <div
            key={item.id}
            className="absolute pointer-events-none"
            style={{
              left: `${item.x}px`,
              top: `${item.y}px`,
              transform: 'translate(-50%, -50%)',
              opacity: opacity,
              transition: 'opacity 0.5s ease-out',
              zIndex: item.zIndex
            }}
          >
            {item.isImage ? (
              <div className="pixelated-image">
                <img
                  src={`${item.content.imageUrl}?w=${imageSize}&h=${imageSize}&fit=crop`}
                  alt={item.content.name}
                  className="w-[5.67rem] h-[5.67rem] md:w-[6.3rem] md:h-[6.3rem] object-cover"
                  loading="lazy"
                  decoding="async"
                  style={{
                    imageRendering: 'pixelated',
                    filter: 'contrast(1.1) saturate(1.2)'
                  }}
                />
              </div>
            ) : (
              <span 
                className="text-lg md:text-2xl font-medium text-[#1A532F]/60 whitespace-nowrap"
                style={{ fontFamily: "'VT323', monospace" }}
              >
                {item.content.text}
              </span>
            )}
          </div>
        );
      })}

      {/* Left-aligned Logo with emerge animation - text at z-10 */}
      <div className="relative z-10 w-full max-w-[92vw] px-4 sm:px-6 md:max-w-none md:pl-16 animate-emerge">
        <h1 
          className="text-[2.2rem] sm:text-[3.8rem] md:text-[7rem] lg:text-[9rem] xl:text-[11rem] font-bold text-[#1A532F] leading-[0.95] md:leading-none text-left"
          style={{ fontFamily: "'Press Start 2P', monospace" }}
        >
          Unmapped
          <span className="block text-[#5B9BD5]">.blr</span>
        </h1>
        
        <p className="text-sm sm:text-lg md:text-xl text-[#1A532F]/70 font-light mt-4 md:mt-8 max-w-[90vw] sm:max-w-2xl uppercase text-left md:text-left">
          A Project to Reclaim the City..
        </p>
      </div>

      {/* Decorative corner elements */}
      <a
        href={gamesData.gameLink}
        target="_blank"
        rel="noopener noreferrer"
        className="hero-play-diamond absolute top-8 right-8 z-30 flex w-20 h-20 md:w-24 md:h-24 items-center justify-center border-4 border-[#FFE39A] bg-[#FFC247] rounded-lg rotate-45 text-white font-bold text-[0.92rem] md:text-[1.02rem] uppercase leading-tight text-center hover:bg-[#FFD05F] hover:border-white transition-all duration-200"
        style={{ fontFamily: "'VT323', monospace" }}
      >
        <span className="-rotate-45">
          Play
          <br />
          Unmap
        </span>
      </a>
      {showDebug && (
        <div className="fixed bottom-4 left-4 z-50 bg-black/75 text-white text-xs px-3 py-2 rounded">
          mode: {isCoarsePointer ? 'mobile' : 'desktop'} | items: {trail.length} | motion: {prefersReducedMotion ? 'reduced' : 'full'}
        </div>
      )}
    </section>
  );
};

export default HeroSection;
